import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  useTheme,
  alpha,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Stack,
  Collapse
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Category as CategoryIcon,
  Inventory as InventoryIcon,
  TrendingUp as TrendingUpIcon,
  Business as CompanyIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  ExpandMore,
  FilterList
} from '@mui/icons-material';
import { usePageTitle } from 'contexts/PageTitleContext';
import FlexBetween from 'components/flexbox/FlexBetween';
import SearchInput from 'components/input-fields/SearchInput';
import apiService from 'Network/apiService';
import { API_ENDPOINTS } from 'Network/apiEndpoints';
import ProductsList from './ProductsList';
import AddProductModal from './AddProductModal';
import EditProductModal from './EditProductModal';
import { useNavigate } from 'react-router-dom';
import AppPagination from 'components/AppPagination';
import buttonConfig from "config/ConfigAccessButton";

const ProductsManagement = () => {
  const theme = useTheme();
  const { setPageTitle } = usePageTitle();
  const navigate = useNavigate();

  // Check if user is Company type
  const isCompanyUser = apiService.isCompanyUser();
  const companyId = apiService.getCompanyId();
  const companyDetails = apiService.getCompanyDetails();
  
  // State
  const [companies, setCompanies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(isCompanyUser ? companyId : 'all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [addProductModalOpen, setAddProductModalOpen] = useState(false);
  const [editProductModalOpen, setEditProductModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    current_page: 1,
    page_size: 10,
    total_items: 0,
    total_pages: 1,
    has_next: false,
    has_previous: false,
    next_page_number: null,
    previous_page_number: null
  });

  // Stats state
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalCategories: 0,
    totalStock: 0,
    totalSold: 0
  });

  // ============ PERMISSION FUNCTIONS ============
const getCurrentButtonPermissions = () => {
  const moduleConfig = buttonConfig["Product Management"];
  console.log("Full Button Config:", buttonConfig);
  console.log("Current Button Permissions:", moduleConfig);
  return moduleConfig || {};  // Return the module config directly, not .actions
};
  
  const shouldEnableButton = (buttonKey) => {
    const permissions = getCurrentButtonPermissions();
    return permissions[buttonKey] === true;
  };
  // ============ END PERMISSION FUNCTIONS ============

  useEffect(() => {
    setPageTitle('Product Management');
  }, [setPageTitle]);
  
  // Fetch companies
  const fetchCompanies = async () => {
    try {
      if (isCompanyUser && companyDetails) {
        setCompanies([companyDetails]);
        setStats(prev => ({ ...prev, totalCompany: 1 }));
        return;
      }
      
      const res = await apiService.get(API_ENDPOINTS.GET_company);
      if (res?.success) {
        const companyList = res.data?.Company || [];
      setCompanies(companyList);
      setStats(prev => ({ ...prev, totalCompany: companyList.length }));
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  // Fetch categories (optionally by company)
  const fetchCategories = async (companyId = 'all') => {
    try {
      let response;
      if (companyId === 'all') {
        response = await apiService.get(API_ENDPOINTS.GET_product_categories);
      } else {
        const userId = localStorage.getItem('user_id');
        response = await apiService.get(`${API_ENDPOINTS.GET_product_category}?company=${companyId}&user_id=${userId}`);
      }
      if (response?.success) {
        const list = response.data?.ProductCategory || [];
        setCategories(list);
        setStats(prev => ({ ...prev, totalCategories: list.length }));
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Fetch products by filters
  const fetchProducts = async (page = 1, pageSize = null, searchValue = null) => {
    setIsLoading(true);
    try {
      const userId = localStorage.getItem('user_id');
      const currentPageSize = pageSize || pagination.page_size;
      const currentSearch = searchValue !== null ? searchValue : searchTerm;
      
      let url = `${API_ENDPOINTS.GET_all_products}?user_id=${userId}&page=${page}&page_size=${currentPageSize}`;
      if (selectedCompany !== 'all') url += `&company=${selectedCompany}`;
      if (selectedCategory !== 'all') url += `&category=${selectedCategory}`;
      if (currentSearch.trim()) url += `&search=${encodeURIComponent(currentSearch.trim())}`;

      const response = await apiService.get(url);
      if (response?.success) {
        const productsData = response.data.Product || [];
        setProducts(productsData);
        
        const paginationData = response.pagination || response.data?.pagination || {};
        if (Object.keys(paginationData).length > 0) {
          setPagination(prev => ({
            ...prev,
            current_page: paginationData.current_page ?? page,
            page_size: paginationData.page_size ?? currentPageSize,
            total_items: paginationData.total_items ?? 0,
            total_pages: paginationData.total_pages ?? 1,
            has_next: paginationData.has_next ?? false,
            has_previous: paginationData.has_previous ?? false,
            next_page_number: paginationData.next_page_number ?? null,
            previous_page_number: paginationData.previous_page_number ?? null
          }));
        }
        
        const totalStock = productsData.reduce((sum, product) => 
          sum + parseFloat(product.analytics?.total_stock || 0), 0);
        const totalSold = response.stats?.total_ordered_item_quantities ? parseFloat(response.stats.total_ordered_item_quantities) : 0;
        setStats(prev => ({
          ...prev,
          totalProducts: paginationData.total_items ?? productsData.length,
          totalStock: totalStock,
          totalSold: totalSold
        }));
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial loads - only run once
  useEffect(() => {
    const initializeData = async () => {
      await fetchCompanies();
      if (isCompanyUser && companyId) {
        await fetchCategories(companyId);
      } else {
        await fetchCategories('all');
      }
    };
    
    initializeData();
  }, []);

  // Refetch categories when company changes (only for Admin users)
  useEffect(() => {
    if (!isCompanyUser && selectedCompany) {
      fetchCategories(selectedCompany);
      setSelectedCategory('all');
    }
  }, [selectedCompany, isCompanyUser]);

  // Refetch products when filters change
  useEffect(() => {
    if (selectedCompany) {
      fetchProducts(1);
    }
  }, [selectedCategory, selectedCompany, refreshKey, searchTerm]);

  const handleCompanyChange = (event) => {
    setSelectedCompany(event.target.value);
  };

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
    fetchCompanies();
    if (isCompanyUser && companyId) {
      fetchCategories(companyId);
    } else {
      fetchCategories(selectedCompany);
    }
  };

  const handleAddProduct = () => {
    setAddProductModalOpen(true);
  };

  const handleProductAdded = () => {
    setRefreshKey(prev => prev + 1);
    setAddProductModalOpen(false);
  };

  const handleEditProduct = (product) => {
    setSelectedProduct(product);
    setEditProductModalOpen(true);
  };

  const handleProductUpdated = () => {
    setRefreshKey(prev => prev + 1);
    setEditProductModalOpen(false);
    setSelectedProduct(null);
  };

  const handlePageChange = (_, page) => {
    fetchProducts(page);
  };

  const onPageSizeChange = (event) => {
    const newSize = Number(event.target.value);
    setPagination((prev) => ({
      ...prev,
      page_size: newSize,
      current_page: 1
    }));
    fetchProducts(1, newSize);
  };

  const handleSearch = () => {
    const value = searchQuery.trim();
    if (!value) {
      handleClearSearch();
      return;
    }
    setSearchTerm(value);
    setHasSearched(true);
    fetchProducts(1, undefined, value);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchTerm('');
    setHasSearched(false);
    fetchProducts(1, undefined, '');
  };

  const statsCards = [
    {
      title: 'Total Products',
      value: stats.totalProducts,
      icon: <InventoryIcon />,
      color: theme.palette.primary.main,
      bgColor: alpha(theme.palette.primary.main, 0.1)
    },
    {
      title: 'Categories',
      value: stats.totalCategories,
      icon: <CategoryIcon />,
      color: theme.palette.secondary.main,
      bgColor: alpha(theme.palette.secondary.main, 0.1)
    },
    {
      title: 'Total Company',
      value: stats.totalCompany || 0,
      icon: <CompanyIcon />,
      color: theme.palette.info.main,
      bgColor: alpha(theme.palette.info.main, 0.1)
    },
    {
      title: 'Total Sold Quantity',
      value: Math.round(stats.totalSold),
      icon: <TrendingUpIcon />,
      color: theme.palette.success.main,
      bgColor: alpha(theme.palette.success.main, 0.1)
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {statsCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                p: 2.5,
                background: `linear-gradient(135deg, ${stat.bgColor} 0%, ${alpha(stat.color, 0.05)} 100%)`,
                border: `1px solid ${alpha(stat.color, 0.1)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 8px 25px ${alpha(stat.color, 0.15)}`
                }
              }}
            >
              <FlexBetween>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {stat.title}
                  </Typography>
                  <Typography variant="h4" fontWeight={700} color={stat.color}>
                    {stat.value.toLocaleString()}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    backgroundColor: alpha(stat.color, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: stat.color
                  }}
                >
                  {stat.icon}
                </Box>
              </FlexBetween>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Controls */}
      <Card sx={{ mb: 3 }}>
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
            background: alpha(theme.palette.primary.main, 0.02),
            borderRadius: '16px 16px 0 0',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <FilterList color="primary" />
              <Typography variant="h6" fontWeight={600}>
                Filter & Action Buttons
              </Typography>
            </Box>
            <IconButton
              onClick={() => setShowFilters(!showFilters)}
              sx={{
                transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease',
              }}
            >
              <ExpandMore />
            </IconButton>
          </Box>
        </Box>

        <Collapse in={showFilters}>
        <Box sx={{ p: 3 }}>
        {/* Filters Section */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>Filters</Typography>
            <Button
              variant="contained"
              onClick={handleRefresh}
              startIcon={<RefreshIcon />}
              sx={{ 
                minWidth: "auto",
                px: 2,
                borderRadius: 2,
                height: 40,
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: 'none'
                }
              }}
            >
              Refresh
            </Button>
          </Box>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3.5 }>
              <TextField
                fullWidth
                placeholder="Search products by name, SKU, description..."
                value={searchQuery}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchQuery(value);
                  if (hasSearched && value !== searchTerm) {
                    setHasSearched(false);
                  }
                  if (!value.trim() && (searchTerm || hasSearched)) {
                    handleClearSearch();
                  }
                }}
                onKeyPress={(event) => {
                  if (event.key === 'Enter') {
                    handleSearch();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton 
                        size="small" 
                        onClick={handleClearSearch} 
                        edge="end"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: { height: 40 }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: 40,
                    borderRadius: 2
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSearch}
                  startIcon={<SearchIcon />}
                  disabled={!searchQuery.trim() || (searchQuery === searchTerm && hasSearched)}
                  sx={{ 
                    borderRadius: 2, 
                    textTransform: "none", 
                    height: 40,
                    boxShadow: 'none',
                    '&:hover': {
                      boxShadow: 'none'
                    }
                  }}
                >
                  Search
                </Button>
                
                <FormControl size="small" sx={{ minWidth: 110 }}>
                  <InputLabel>Rows</InputLabel>
                  <Select 
                    value={pagination.page_size} 
                    label="Rows" 
                    onChange={onPageSizeChange}
                  >
                    {[10, 25, 50, 100].map((opt) => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Company</InputLabel>
                <Select 
                  value={selectedCompany} 
                  label="Company" 
                  onChange={handleCompanyChange}
                  disabled={isCompanyUser}
                >
                  {!isCompanyUser && (
                    <MenuItem value="all">
                      <Chip label="All Companies" size="small" color="default" sx={{ mr: 1 }} />
                      All Companies
                    </MenuItem>
                  )}
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      <Chip label={company.name} size="small" color="secondary" variant="outlined" sx={{ mr: 1 }} />
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select value={selectedCategory} label="Category" onChange={handleCategoryChange}>
                  <MenuItem value="all">
                    <Chip label="All Categories" size="small" color="default" sx={{ mr: 1 }} />
                    All Categories
                  </MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      <Chip label={category.company.name} size="small" color="primary" variant="outlined" sx={{ mr: 1 }} />
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        {/* Actions Section */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>Actions</Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddProduct}
              disabled={!shouldEnableButton('add_product')}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                opacity: !shouldEnableButton('add_product') ? 0.6 : 1,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`
                }
              }}
            >
              Add Product
            </Button>
            <Button
              variant="outlined"
              startIcon={<CategoryIcon />}
              onClick={() => navigate('/dashboard/company-category-management')}
              disabled={!shouldEnableButton('manage_companies_and_categories')}
              sx={{ 
                borderRadius: 2, 
                textTransform: 'none', 
                fontWeight: 600, 
                px: 3, 
                py: 1.5,
                opacity: !shouldEnableButton('manage_companies_and_categories') ? 0.6 : 1
              }}
            >
              Manage Companies & Categories
            </Button>
          </Box>
        </Box>
        </Box>
        </Collapse>
      </Card>

      {/* Products List */}
      <Card>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <ProductsList
            products={products}
            categories={categories}
            searchQuery={searchTerm}
            selectedCategory={selectedCategory}
            onRefresh={handleRefresh}
            onEditProduct={handleEditProduct}
          />
        )}
      </Card>

      {/* Pagination */}
      <Box display="flex" flexDirection="column" alignItems="center" gap={1} mt={2}>
        <Typography variant="body2" color="text.secondary">
          {pagination.total_items > 0
            ? `Showing ${(pagination.current_page - 1) * pagination.page_size + 1} - ${Math.min(pagination.current_page * pagination.page_size, pagination.total_items)} of ${pagination.total_items}`
            : 'No products'}
        </Typography>
        {pagination.total_pages > 1 && (
          <Stack alignItems="center">
            <AppPagination
              shape="rounded"
              count={pagination.total_pages}
              page={pagination.current_page}
              onChange={handlePageChange}
            />
          </Stack>
        )}
      </Box>

      {/* Add Product Modal */}
      <AddProductModal
        open={addProductModalOpen}
        onClose={() => setAddProductModalOpen(false)}
        onProductAdded={handleProductAdded}
        categories={categories}
      />

      {/* Edit Product Modal */}
      <EditProductModal
        open={editProductModalOpen}
        onClose={() => {
          setEditProductModalOpen(false);
          setSelectedProduct(null);
        }}
        onProductUpdated={handleProductUpdated}
        product={selectedProduct}
      />
    </Box>
  );
};

export default ProductsManagement;